import type { UniversityRepository } from "@src/domain/universities/repository/university_repository";
import type { UniversityDTO, UpdateUniversityInputDTO } from "../dto/university.dto";
import { University } from "@src/domain/universities/entities/university.entity";
import { UniqueEntityID } from "@src/domain/pascos/value_objects/unique_entity_Id";

/**
 * Use Case: Update University
 */
export class UpdateUniversityUseCase {
    constructor(private readonly universityRepository: UniversityRepository) { }

    async execute(data: UpdateUniversityInputDTO): Promise<UniversityDTO> {
        this.validateUpdateData(data);

        // Find existing university
        const existingUniversity = await this.universityRepository.findById(data.universityId);
        if (!existingUniversity) {
            throw new Error('University not found');
        }

        // Helper functions for clean null/undefined handling
        const updateRequired = <T>(newValue: T | null | undefined, existingValue: T): T => {
            return newValue != null ? newValue : existingValue;
        };

        const updateOptional = <T>(newValue: T | null | undefined, existingValue: T | null | undefined): T | null | undefined => {
            return newValue !== undefined ? newValue : existingValue;
        };

        // Create updated university entity
        const updatedUniversity = University.create({
            name: updateRequired(data.name, existingUniversity.name),
            code: updateRequired(data.code, existingUniversity.code),
            description: updateOptional(data.description, existingUniversity.description),
            address: updateOptional(data.address, existingUniversity.address),
            website: updateOptional(data.website, existingUniversity.website),
            isActive: updateRequired(data.is_active, existingUniversity.isActive),
            createdAt: existingUniversity.createdAt,
            updatedAt: new Date(),
        }, new UniqueEntityID(data.universityId));

        await this.universityRepository.update(updatedUniversity);

        return {
            university_id: updatedUniversity.universityId,
            name: updatedUniversity.name,
            code: updatedUniversity.code,
            description: updatedUniversity.description,
            address: updatedUniversity.address,
            website: updatedUniversity.website,
            is_active: updatedUniversity.isActive,
            created_at: updatedUniversity.createdAt.toISOString(),
            updated_at: updatedUniversity.updatedAt.toISOString(),
        };
    }

    private validateUpdateData(data: UpdateUniversityInputDTO): void {
        if (!data.universityId) {
            throw new Error('University ID is required');
        }

        if (data.name !== undefined && (!data.name || !data.name.trim())) {
            throw new Error('University name cannot be empty');
        }

        if (data.code !== undefined && (!data.code || !data.code.trim())) {
            throw new Error('University code cannot be empty');
        }
    }
}